/* eslint-disable jsx-a11y/no-noninteractive-element-to-interactive-role */
/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import React, { useEffect, useReducer, useRef, useState } from "react";
import StackGrid from "react-stack-grid";
import { toJson } from "unsplash-js";
import { unsplash, Trans } from "app/utils";
import { Button, SearchBar, Loader } from "@culturehq/components";

const SEARCH_DELAY = 300;
const FIRST_PAGE = 1;
const NUMBER_OF_ITEMS = 20;
const MAX_NUMBER_OF_PAGES = 1000;
const initialState = {
  appending: null,
  error: null,
  fetching: false,
  images: [],
  pagination: {
    currentPage: 1
  },
  validSearchValue: true
};

const reducer = (state, action) => {
  switch (action.type) {
    case "appendStart":
      return { ...state, appending: state.pagination.currentPage + 1 };
    case "appendSuccess":
      return {
        ...state,
        appending: null,
        pagination: {
          ...state.pagination,
          currentPage: state.pagination.currentPage + 1,
          totalPages: action.json.total_pages
        },
        images: [...state.images, ...action.json.results]
      };
    case "appendError":
      return { ...state, appending: null, error: action.error };
    case "fetchStart":
      return { ...state, error: null, fetching: true };
    case "fetchSuccess":
      return {
        ...state,
        error: null,
        fetching: false,
        pagination: {
          ...state.pagination,
          currentPage: state.pagination.currentPage + 1,
          totalPages: action.json.total_pages
        },
        images: action.json.results,
        validSearchValue: action.validSearchValue
      };
    case "fetchError":
      return { ...state, error: action.error, fetching: false };
    default:
      return state;
  }
};

const getPhotosWithNoFilter = dispatch => {
  dispatch({ type: "fetchStart" });
  unsplash.photos.listPhotos(1, NUMBER_OF_ITEMS)
    .then(toJson)
    .then(json => {
      dispatch({ type: "fetchSuccess", json: { results: json, total_pages: MAX_NUMBER_OF_PAGES }, validSearchValue: false });
    })
    .catch(error => {
      dispatch({ type: "fetchError", error });
    });
};

const useFetchInitialImages = (dispatch, search) => useEffect(
  () => {
    dispatch({ type: "fetchStart" });
    if (search) {
      unsplash.search.photos(search, FIRST_PAGE, NUMBER_OF_ITEMS)
        .then(toJson)
        .then(json => {
          if (json.total === 0) {
            getPhotosWithNoFilter(dispatch);
          } else {
            dispatch({ type: "fetchSuccess", json, validSearchValue: true });
          }
        })
        .catch(error => {
          dispatch({ type: "fetchError", error });
        });
    } else {
      getPhotosWithNoFilter(dispatch);
    }
  },
  [dispatch, search]
);

const FindImageOnUnsplash = ({
  search, showSearch, onSelectImage, inline = false, maxHeight = 370, searchBarRef = {}
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [searchValue, setSearchValue] = useState(search);
  useFetchInitialImages(dispatch, searchValue);
  let timeout = 0;

  const performSearch = value => {
    setSearchValue(value);
    unsplash.search
      .photos(value, FIRST_PAGE, NUMBER_OF_ITEMS)
      .then(toJson)
      .then(json => {
        dispatch({ type: "fetchSuccess", json });
      })
      .catch(error => {
        dispatch({ type: "fetchError", error });
      });
  };

  const handleOnSearch = value => {
    const MIN_CHARS_TO_SEARCH = 3;

    if (timeout) {
      clearTimeout(timeout);
    }

    if (value.length >= MIN_CHARS_TO_SEARCH) {
      timeout = setTimeout(() => performSearch(value), SEARCH_DELAY);
    }
  };

  const appendImagesWithNoFilter = () => {
    unsplash.photos
      .listPhotos(state.pagination.currentPage, NUMBER_OF_ITEMS)
      .then(toJson)
      .then(json => {
        dispatch({
          type: "appendSuccess",
          json: { results: json, total_pages: MAX_NUMBER_OF_PAGES }
        });
      })
      .catch(error => {
        dispatch({ type: "appendError", error });
      });
  };

  const appendImages = () => {
    if (searchValue && state.validSearchValue) {
      unsplash.search
        .photos(searchValue, state.pagination.currentPage, NUMBER_OF_ITEMS)
        .then(toJson)
        .then(json => {
          if (json.total === 0) {
            appendImagesWithNoFilter();
          } else {
            dispatch({ type: "appendSuccess", json });
          }
        })
        .catch(error => {
          dispatch({ type: "appendError", error });
        });
      return;
    }
    appendImagesWithNoFilter();
  };

  const handleOnScroll = event => {
    const { currentTarget } = event;
    if (
      currentTarget.scrollTop + maxHeight >= currentTarget.scrollHeight
      && !state.appending
    ) {
      dispatch({ type: "appendStart" });
      appendImages();
    }
  };

  return (
    <div className={`Unsplash--Search ${inline && "Unsplash--Search-inline"}`}>
      <div className="Unsplash--Search__Title">
        <Button icon="chevron-left" onClick={() => showSearch(false)} />
        <h4>
          <Trans>
            Photos by <span className="underlined">Unsplash</span>
          </Trans>
        </h4>
      </div>
      <SearchBar ref={searchBarRef} autoFocus onSearchChange={handleOnSearch} onSearch={() => {}} placeholder="Search images by topic" />
      <Loader loading={state.fetching}>
        <div className="images-results" onScroll={handleOnScroll}>
          <StackGrid columnWidth={140} monitorImagesLoaded>
            {state.images.map(image => (
              <div
                tabIndex={0}
                key={image.id}
                className="image-result"
                onClick={() => { onSelectImage(image); }}
                onKeyDown={e => { if (e.key === "Enter") onSelectImage(image); }}
              >
                <img className="image" src={image.urls.thumb} alt={image.alt_description} />
                <a className="image-author" href={`${image.user.links.html}?utm_source=culturehq&utm_medium=referral`} target="_blank" rel="noopener noreferrer">{`${image.user.first_name} ${image.user.last_name || ""}`}</a>
              </div>
            ))}
          </StackGrid>
        </div>
      </Loader>
    </div>
  );
};

export default FindImageOnUnsplash;
